import { useCallback, useEffect, useRef } from 'react';
import type {
  AnyVariables,
  DocumentInput,
  OperationContext,
  OperationResult,
} from 'urql';
import { useMutation } from 'urql';

const OPERATION_ABORTED = 'Operation was aborted';

/**
 * Custom error class for cancelled operations (React Native compatible)
 */
export class AbortError extends Error {
  name = 'AbortError';

  constructor(message = OPERATION_ABORTED) {
    super(message);
  }
}

/**
 * A hook that provides a cancellable mutation with automatic cleanup.
 *
 * @param mutation - GraphQL mutation document
 * @returns Tuple of [mutationState, executeCancellableMutation, abortCurrentMutation]
 *
 * @example
 * ```ts
 * const [state, execute, abort] = useCancellableMutation(MyMutation);
 *
 * const handleSubmit = async () => {
 *   try {
 *     const result = await execute({ input: data });
 *     if (!result.error) {
 *       router.push('/success');
 *     }
 *   } catch (error) {
 *     if (error instanceof AbortError) {
 *       // Operation was cancelled
 *       return;
 *     }
 *     // Handle other errors
 *   }
 * };
 * ```
 */
export function useCancellableMutation<
  Data = Record<string, unknown>,
  Variables extends AnyVariables = AnyVariables,
>(mutation: DocumentInput<Data, Variables>) {
  const [state, executeMutation] = useMutation<Data, Variables>(mutation);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const executeCancellableMutation = useCallback(
    async (
      variables: Variables,
      context?: Partial<OperationContext>,
    ): Promise<OperationResult<Data, Variables>> => {
      // Cancel any previous operation
      abortControllerRef.current?.abort();

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      try {
        // Execute mutation with abort signal
        const result = await executeMutation(variables, {
          ...context,
          fetchOptions: {
            ...context?.fetchOptions,
            signal: abortControllerRef.current.signal,
          },
        });

        // Check if operation was aborted during execution
        if (abortControllerRef.current.signal.aborted) {
          throw new AbortError(OPERATION_ABORTED);
        }

        return result;
      } catch (error) {
        // If aborted, throw our custom AbortError
        if (abortControllerRef.current?.signal.aborted) {
          throw new AbortError(OPERATION_ABORTED);
        }
        throw error;
      }
    },
    [executeMutation],
  );

  const abortCurrentMutation = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  return [state, executeCancellableMutation, abortCurrentMutation] as const;
}

export default useCancellableMutation;
